import { useContext, useEffect, useState, type FC } from "react";
import MainLayout from "../components/MainLayout";
import { useParams } from "react-router-dom";
import { LoadingContext } from "../contexts/LoadingContext";
import { getSaleDetail, getSaleDownload } from "../services/sales/SalesApi";
import { errorToast } from "../utils/helper-ui";
import { Sales } from "../models/sales/sales";
import { Divider, IconButton, Steps } from "rsuite";
import Moment from "react-moment";
import { money, statusColorMap } from "../utils/helper";
import { BsLink, BsLink45Deg } from "react-icons/bs";
import DownloadIcon from "@rsuite/icons/FileDownload";

interface SalesDetailProps {}

const SalesDetail: FC<SalesDetailProps> = ({}) => {
  const { salesId } = useParams();
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [mounted, setMounted] = useState(false);
  const [sales, setSales] = useState<Sales>();

  useEffect(() => {
    setMounted(true);

    return () => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;
    fetchData();
  }, [mounted, salesId]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await getSaleDetail(salesId!);
      const resJson = await res.json();
      //   console.log(resJson.data);
      setSales(resJson.data);
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeader = () => (
    <div className="flex flex-row justify-between items-center px-2">
      <h1 className="text-2xl font-semibold">Sales Detail</h1>
    </div>
  );

  return (
    <MainLayout header={renderHeader()}>
      <div className="invoice-detail">
        <div className="flex flex-row justify-between">
          <h2 className="text-2xl font-bold ">{sales?.sales_number}</h2>
          <IconButton
            size="sm"
            appearance="primary"
            color="blue"
            icon={<DownloadIcon />}
            onClick={() => {
              getSaleDownload(salesId!)
                .then((res) => {
                  return res.blob();
                })
                .then((blob) => {
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${sales?.sales_number}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                })
                .catch((error) => {
                  errorToast(`${error}`);
                });
            }}
          >
            Download
          </IconButton>
        </div>
        <Divider />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="mb-2">
              <p className="text-gray-400">Date</p>
              <h4 className=" text-gray-800">
                <Moment format="DD MMMM YYYY">{sales?.sales_date}</Moment>
              </h4>
            </div>
            <div className="mb-2">
              <p className="text-gray-400">Merchant</p>
              <h4 className=" text-gray-800">{sales?.merchant?.name}</h4>
              <p className="text-sm">{sales?.merchant?.address}</p>
              <p className="text-sm">{sales?.merchant?.phone}</p>
            </div>
          </div>
          <div>
            <div className="mb-2">
              <p className="text-gray-400">Due Date</p>
              <h4 className=" text-gray-800">
                <Moment format="DD MMMM YYYY">{sales?.due_date}</Moment>
              </h4>
            </div>
            <div className="mb-2">
              <p className="text-gray-400">Shipped To</p>
              <h4 className=" text-gray-800">
                {sales?.data_contact?.full_name}
              </h4>
              <p className="text-sm">{sales?.data_contact?.shipping_address}</p>
              <p className="text-sm">{sales?.data_contact?.phone_number}</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="mb-2">
              <p className="text-gray-400">Status</p>
              <div
                className={`${
                  statusColorMap[sales?.status as keyof typeof statusColorMap]
                } w-fit px-4 min-w-24 text-center py-1 text-white text-sm rounded-2xl`}
              >
                {sales?.status.toUpperCase()}
              </div>
            </div>
            {sales?.payment?.payment_link && (
              <div className="mb-2">
                <p className="text-gray-400">Payment Link</p>
                <h4
                  className=" text-gray-800 cursor-pointer hover:underline text-xs"
                  onClick={() =>
                    window.open(sales?.payment?.payment_link, "_blank")
                  }
                >
                  {sales?.payment?.payment_link}
                  <BsLink45Deg className="inline ml-2" />
                </h4>
              </div>
            )}
          </div>
          <div>
            {/* <div className="mb-2">
              <p className="text-gray-400">Payment Status</p>
              <h4 className=" text-gray-800">{sales?.payment?.status}</h4>
            </div> */}
            <div className="mb-2">
              <p className="text-gray-400">User Payment Status</p>
              <div
                className={`${
                  statusColorMap[
                    sales?.user_payment_status as keyof typeof statusColorMap
                  ]
                } w-fit px-4 min-w-24 text-center py-1 text-white text-sm rounded-2xl`}
              >
                {sales?.user_payment_status}
              </div>
            </div>
          </div>
        </div>
        <table className="w-full mt-8">
          <thead>
            <tr>
              <th className="px-2 py-1 text-sm  text-left bg-gray-200">Item</th>
              <th className="px-2 py-1 text-sm  text-right bg-gray-200">
                Quantity
              </th>
              <th className="px-2 py-1 text-sm  text-right bg-gray-200">
                Price
              </th>
              <th className="px-2 py-1 text-sm  text-right bg-gray-200">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {(sales?.items ?? []).map((item, index) => (
              <tr key={index}>
                <td className="px-2 py-2 text-normal  text-left">
                  {item.product?.name}
                </td>
                <td className="px-2 py-2 text-normal  text-right">
                  {money(item.quantity)}
                </td>
                <td className="px-2 py-2 text-normal  text-right">
                  {money(item.unit_price)}
                  {item.unit_price_before_discount > 0 && (
                    <>
                    <br />
                    <span className="text-gray-400 text-xs line-through">
                      {money(item.unit_price_before_discount)}
                    </span>
                    </>
                  )}
                </td>
                <td className="px-2 py-2 text-normal  text-right">
                  {money(item.subtotal)}
                  {item.subtotal_before_disc > 0 && (
                    <>
                    <br />
                    <span className="text-gray-400 text-xs line-through">
                      {money(item.subtotal_before_disc)}
                    </span>
                    </>
                  )}
                </td>
              </tr>
            ))}
            <tr className="border-t">
              <td className="px-2 py-2 text-normal  text-left"></td>
              <td className="px-2 py-2 text-normal  text-right"></td>
              <td className="px-2 py-2 text-sm  text-right font-bold">
                Sub Total
              </td>
              <td className="px-2 py-2 text-normal  text-right font-bold">
                {money(sales?.subtotal)}
              </td>
            </tr>
            <tr className="">
              <td className="px-2 py-2 text-normal  text-left"></td>
              <td className="px-2 py-2 text-normal  text-right"></td>
              <td className="px-2 py-2 text-sm  text-right font-bold">
                Tax {money(sales?.tax)}%
              </td>
              <td className="px-2 py-2 text-normal  text-right font-bold">
                {money(sales?.tax_amount)}
              </td>
            </tr>
            <tr className="">
              <td className="px-2 py-2 text-normal  text-left"></td>
              <td className="px-2 py-2 text-normal  text-right"></td>
              <td className="px-2 py-2 text-sm  text-right font-bold">
                Total Discount
              </td>
              <td className="px-2 py-2 text-normal  text-right font-bold">
                {money((sales?.sub_total_before_discount ?? 0) - (sales?.subtotal ?? 0))}
              </td>
            </tr>
            <tr className="">
              <td className="px-2 py-2 text-normal  text-left"></td>
              <td className="px-2 py-2 text-normal  text-right"></td>
              <td className="px-2 py-2 text-sm  text-right font-bold">
                Service Fee
              </td>
              <td className="px-2 py-2 text-normal  text-right font-bold">
                {money(sales?.service_fee)}
              </td>
            </tr>
            <tr className="">
              <td className="px-2 py-2 text-normal  text-left"></td>
              <td className="px-2 py-2 text-normal  text-right"></td>
              <td className="px-2 py-2 text-sm  text-right font-bold">
                Shipping Fee
              </td>
              <td className="px-2 py-2 text-normal  text-right font-bold">
                {money(sales?.shipping_fee)}
              </td>
            </tr>
            <tr className="">
              <td className="px-2 py-2 text-normal  text-left"></td>
              <td className="px-2 py-2 text-normal  text-right"></td>
              <td className="px-2 py-2 text-sm  text-right font-bold">
                Payment Fee
              </td>
              <td className="px-2 py-2 text-normal  text-right font-bold">
                {money(sales?.payment?.payment_fee)}
              </td>
            </tr>

            <tr className="border-t">
              <td className="px-2 py-2 text-normal  text-left"></td>
              <td className="px-2 py-2 text-normal  text-right"></td>
              <td className="px-2 py-2 text-sm  text-right font-bold">Total</td>
              <td className="px-2 py-2 text-normal  text-right font-bold">
                {money(sales?.total)}
              </td>
            </tr>
          </tbody>
        </table>
        {sales?.shipping && (
          <div className="grid grid-cols-2 gap-4 mt-8">
            <div>
              <div className="mb-4">
                <h3 className="text-xl font-bold ">Shipping</h3>
                <p className="text-sm font-bold mt-4 text-gray-600">Provider</p>
                <p className="uppercase">
                  {sales?.shipping?.shipping_data_response?.company}
                </p>
                <p className="text-sm font-bold mt-4 text-gray-600">
                  Service Type
                </p>
                <p className="uppercase">
                  {sales?.shipping?.shipping_data_response?.type}
                </p>
                <p className="text-sm font-bold mt-4 text-gray-600">
                  Waybill ID
                </p>
                <p className="uppercase">
                  {sales?.shipping?.shipping_data_response?.waybill_id}
                </p>
                <p className="text-sm font-bold mt-4 text-gray-600">Link</p>
                <p
                  className=" text-gray-800 cursor-pointer hover:underline text-xs"
                  onClick={() =>
                    window.open(
                      sales?.shipping?.shipping_data_response?.link,
                      "_blank"
                    )
                  }
                >
                  {sales?.shipping?.shipping_data_response?.link}
                  <BsLink45Deg className="inline ml-2" />
                </p>
                <p className="text-sm font-bold mt-4 text-gray-600">Status</p>
                <p className="uppercase font-bold">{sales?.shipping?.status.toUpperCase()}</p>
              </div>
              <div className="mb-4 border-t py-4">
                <h3 className="text-xl font-bold ">Courier</h3>
                <p>{sales?.shipping?.courier?.driver_name}</p>
                <p>{sales?.shipping?.courier?.driver_phone}</p>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold ">Tracking</h3>
              <Steps
                color="green"
                current={sales?.shipping?.tracking_statuses?.length - 1}
                vertical
                style={{
                  width: "200px",
                  display: "inline-table",
                  verticalAlign: "top",
                }}
              >
                {sales?.shipping?.tracking_statuses?.map((item, index) => (
                  <Steps.Item
                    title={item.status.replaceAll("_", " ").toUpperCase()}
                    description={
                      <Moment className="text-xs" format="DD MMM YYYY HH:mm">
                        {item.updated_at}
                      </Moment>
                    }
                    key={index}
                  />
                ))}
              </Steps>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};
export default SalesDetail;
